import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Brand theme provider.
 *
 * On first paint:
 *   1. Sets the default theme ("clinical-teal") on <html data-theme=...> so the
 *      first render has the right colours even before the API responds.
 *   2. Fires a GET /api/site/theme request, then patches the DOM accordingly.
 *
 * For custom themes the API also returns `customTokens` (a flat dict of CSS
 * variable values). We apply them via root.style.setProperty so they win over
 * the [data-theme="custom"] stub block in theme-tokens.css.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'tmd-site-theme';
const DEFAULT_THEME = 'clinical-teal';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  customTokens: {},
  applyTheme: () => {},
  refresh: () => {},
});

function applyToDocument(theme, customTokens) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme || DEFAULT_THEME);
  // Always clear stale inline overrides first
  const STALE_KEYS = [
    '--color-primary', '--color-primary-dark', '--color-primary-darker',
    '--color-primary-soft', '--color-primary-rgb',
    '--color-secondary', '--color-secondary-dark', '--color-secondary-rgb',
    '--color-accent', '--color-accent-dark', '--color-bg-overlay',
  ];
  STALE_KEYS.forEach((k) => root.style.removeProperty(k));
  if (theme === 'custom' && customTokens && typeof customTokens === 'object') {
    Object.entries(customTokens).forEach(([k, v]) => {
      if (typeof k === 'string' && k.startsWith('--') && typeof v === 'string') {
        root.style.setProperty(k, v);
      }
    });
  }
}

export const BrandThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    try {
      const cached = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (cached?.theme) return cached.theme;
    } catch (_) { /* swallow */ }
    return DEFAULT_THEME;
  });
  const [customTokens, setCustomTokens] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const cached = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (cached?.customTokens) return cached.customTokens;
    } catch (_) { /* swallow */ }
    return {};
  });

  // Apply cached theme immediately
  useEffect(() => {
    applyToDocument(theme, customTokens);
  }, [theme, customTokens]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/site/theme`, {
        // public endpoint — explicit no creds keeps it CDN-cacheable later
        withCredentials: false,
      });
      const nextTheme = data?.theme || DEFAULT_THEME;
      const nextTokens = data?.customTokens || {};
      setTheme(nextTheme);
      setCustomTokens(nextTokens);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
          theme: nextTheme,
          customTokens: nextTokens,
        }));
      } catch (_) { /* swallow */ }
    } catch (err) {
      // Silently keep the cached / default theme — never block first paint.
      console.debug('[BrandThemeProvider] using fallback theme', err?.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyTheme = useCallback((next) => {
    if (next?.theme) setTheme(next.theme);
    setCustomTokens(next?.customTokens || {});
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        theme: next?.theme || DEFAULT_THEME,
        customTokens: next?.customTokens || {},
      }));
    } catch (_) { /* swallow */ }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, customTokens, applyTheme, refresh }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useBrandTheme = () => useContext(ThemeContext);
